import React, { useEffect, useMemo, useState } from "react";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Pencil, Plus, Trash2, IceCream2 } from "lucide-react";
import { levelImg } from "@/components/IceCreamCard";
import FilterChips from "@/components/FilterChips";

export default function AdminHelados() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [sucursales, setSucursales] = useState([]);
  const [sucursalFilter, setSucursalFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", sucursal_id: "" });
  const [saving, setSaving] = useState(false);


  const load = async () => {
    try {
      const [ic, ss] = await Promise.all([
        api.get("/icecreams"),
        api.get("/sucursales"),
      ]);
      setItems(ic.data);
      setSucursales(ss.data);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setEditing(null);
    setForm({
      name: "",
      sucursal_id: user?.sucursal_id || sucursales[0]?.id || "",
    });
    setOpen(true);
  };

  const openEdit = (it) => {
    setEditing(it);
    setForm({ name: it.name, sucursal_id: it.sucursal_id || "" });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("El nombre es obligatorio");
    if (!form.sucursal_id) return toast.error("Selecciona una sucursal");
    setSaving(true);
    try {
      if (editing) {
        const { data } = await api.patch(`/icecreams/${editing.id}`, form);
        setItems((p) => p.map((x) => (x.id === data.id ? data : x)));
        toast.success("Sabor actualizado");
      } else {
        const { data } = await api.post("/icecreams", form);
        setItems((p) => [...p, data]);
        toast.success("Sabor creado");
      }
      setOpen(false);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (it) => {
    if (!window.confirm(`¿Eliminar "${it.name}"?`)) return;
    try {
      await api.delete(`/icecreams/${it.id}`);
      setItems((p) => p.filter((x) => x.id !== it.id));
      toast.success("Eliminado");
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    }
  };

  const filteredItems = useMemo(() => {
    if (sucursalFilter === "all") return items;
    return items.filter((it) => it.sucursal_id === sucursalFilter);
  }, [items, sucursalFilter]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-[#FF7878]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-black text-[#4A3B32]" data-testid="admin-helados-title">
            Sabores de helado
          </h1>
          <p className="text-[#8A796F]">Administra los sabores por sucursal.</p>
        </div>
        <Button
          onClick={openNew}
          data-testid="ic-create-button"
          className="rounded-full bg-[#FF7878] hover:bg-[#FF5C5C] text-white font-bold px-6 active:scale-95 transition-transform"
        >
          <Plus className="w-4 h-4 mr-2" /> Nuevo sabor
        </Button>
      </header>

      {sucursales.length > 1 && (
        <FilterChips
          value={sucursalFilter}
          onChange={setSucursalFilter}
          options={sucursales}
          allLabel="Todas las sucursales"
          testidPrefix="ic-sucursal-filter"
        />
      )}

      {filteredItems.length === 0 ? (
        <div className="bg-white border border-dashed border-[#EADBCE] rounded-3xl py-16 text-center">
          <IceCream2 className="mx-auto w-12 h-12 text-[#EADBCE]" />
          <p className="text-[#8A796F] mt-3">
            {items.length === 0 ? "Aún no hay sabores. Agrega el primero." : "Sin sabores en esta sucursal."}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredItems.map((it) => (
            <div key={it.id} className="bg-white border border-[#EADBCE] rounded-2xl p-4 flex gap-4" data-testid={`ic-row-${it.id}`}>
              <div className="w-20 h-20 rounded-xl bg-[#FCF9F2] p-1.5 shrink-0 flex items-center justify-center">
                <img src={levelImg(it.stock_level)} alt="" className="w-full h-full object-contain" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-display font-bold text-[#4A3B32] truncate">{it.name}</h3>
                <p className="text-xs text-[#8A796F] mt-0.5">
                  {it.stock_level ?? 100}% · {it.sucursal?.name || "Sin sucursal"}
                </p>
                <div className="flex gap-2 mt-3">
                  <Button size="sm" variant="ghost" onClick={() => openEdit(it)} data-testid={`ic-edit-${it.id}`} className="rounded-full">
                    <Pencil className="w-3.5 h-3.5 mr-1" /> Editar
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(it)} data-testid={`ic-delete-${it.id}`} className="rounded-full text-[#FF5C5C] hover:text-[#FF5C5C] hover:bg-[#FFF0F0]">
                    <Trash2 className="w-3.5 h-3.5 mr-1" /> Borrar
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-3xl border-[#EADBCE] sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{editing ? "Editar sabor" : "Nuevo sabor"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nombre del sabor</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Ej. Vainilla, Chocolate, Fresa..."
                className="h-12 rounded-xl border-[#EADBCE]"
                data-testid="ic-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Sucursal</Label>
              <Select
                value={form.sucursal_id}
                onValueChange={(v) => setForm({ ...form, sucursal_id: v })}
              >
                <SelectTrigger className="h-12 rounded-xl border-[#EADBCE]" data-testid="ic-sucursal-select">
                  <SelectValue placeholder="Selecciona una sucursal" />
                </SelectTrigger>
                <SelectContent>
                  {sucursales.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-[#8A796F]">Puedes crear sabores para cualquier sucursal.</p>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setOpen(false)} className="rounded-full">
              Cancelar
            </Button>
            <Button
              onClick={save}
              disabled={saving}
              className="rounded-full bg-[#FF7878] hover:bg-[#FF5C5C] text-white font-bold"
              data-testid="ic-save-button"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : editing ? "Guardar" : "Crear"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
